import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ErrorState } from '@/components/state/error-state';
import { EmptyState } from '@/components/state/empty-state';
import { queryKeys } from '@/lib/query-keys';
import { formatRelative } from '@/lib/format';
import { listConfigs, type ConfigSummary } from '@/features/platform-config/api';
import {
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  namespaceMeta,
  type NamespaceMeta,
} from '@/features/platform-config/registry';

export const Route = createFileRoute('/_authenticated/platform-config/')({
  component: PlatformConfigPage,
});

interface Row {
  summary: ConfigSummary;
  meta: NamespaceMeta;
}

function PlatformConfigPage() {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: queryKeys.platformConfig.list(), queryFn: listConfigs });
  const [search, setSearch] = useState('');

  const grouped = useMemo(() => {
    const rows: Row[] = (query.data?.configs ?? []).map((summary) => ({
      summary,
      meta: namespaceMeta(summary.namespace, summary.description),
    }));
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter(
          (r) =>
            r.summary.namespace.toLowerCase().includes(q) ||
            r.meta.label.toLowerCase().includes(q) ||
            r.meta.description.toLowerCase().includes(q)
        )
      : rows;
    return CATEGORY_ORDER.map((category) => ({
      category,
      rows: filtered
        .filter((r) => r.meta.category === category)
        .sort((a, b) => a.meta.label.localeCompare(b.meta.label)),
    })).filter((g) => g.rows.length > 0);
  }, [query.data, search]);

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex flex-col gap-1.5" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">Platform</span>
        <h1 className="wb-h1">Platform config</h1>
        <p className="wb-sub">Tune runtime feature configuration by namespace.</p>
      </div>

      <div className="wb-toolbar" style={{ marginBottom: 20 }}>
        <div className="wb-toolbar-search" style={{ position: 'relative', maxWidth: 320 }}>
          <Search
            size={15}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--ink-3)',
            }}
          />
          <input
            className="wb-field"
            style={{ paddingLeft: 34 }}
            placeholder="Search namespaces…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="wb-cfggrid">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="wb-skeleton" style={{ height: 72, display: 'block' }} />
          ))}
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : grouped.length === 0 ? (
        <EmptyState
          title="No config namespaces"
          hint={search ? 'No match for your search.' : undefined}
        />
      ) : (
        grouped.map(({ category, rows }) => (
          <section key={category} style={{ marginBottom: 26 }}>
            <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
              {CATEGORY_LABEL[category]}
            </div>
            <div className="wb-cfggrid">
              {rows.map(({ summary, meta }) => {
                const Icon = meta.icon;
                return (
                  <button
                    key={summary.namespace}
                    type="button"
                    className="wb-cfgcard"
                    onClick={() =>
                      navigate({
                        to: '/platform-config/$namespace',
                        params: { namespace: summary.namespace },
                      })
                    }
                  >
                    <span className="wb-cfgcard-icon">
                      <Icon size={18} />
                    </span>
                    <div className="wb-cfgcard-body">
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 3,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{meta.label}</span>
                        <Badge tone={summary.has_override ? 'honey' : 'neutral'}>
                          {summary.has_override ? 'Override' : 'Default'}
                        </Badge>
                      </div>
                      <div className="wb-tiny" style={{ color: 'var(--ink-3)' }}>
                        {meta.description}
                      </div>
                      {summary.has_override && summary.updated_at ? (
                        <div className="wb-tiny" style={{ marginTop: 5 }}>
                          {formatRelative(summary.updated_at)}
                          {summary.updated_by ? ` · ${summary.updated_by}` : ''}
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
